import { reactive } from "vue";
import { fetcher } from "../../api/fetcher";
import { authState } from "../auth";
import { roomState } from "../room";
import { isRecorder } from "../isRecState";

const state = reactive({
  connected: false,
  desired: { camera: true, micro: true },
  synced: { camera: true, micro: true },
});

// Поколение сессии синхронизации: инкремент останавливает незавершённые
// циклы syncDevices (закрытие ws, cleanup).
let generation = 0;

export function apiConnected(device: { camera: boolean; micro: boolean }) {
  state.connected = true;
  state.synced = { ...device };
  void syncDevices();
}

export function apiDisconnected() {
  // Желаемое состояние сохраняется: тумблеры, сделанные во время обрыва ws,
  // доедут после реконнекта (sync_info → apiConnected).
  generation++;
  state.connected = false;
  state.synced = { camera: true, micro: true };
}

/**
 * cleanup(): новая сессия не наследует ни фактическое, ни желаемое
 * api-состояние, а незавершённый цикл синхронизации старой сессии
 * останавливается.
 */
export function apiReset() {
  generation++;
  state.connected = false;
  state.desired = { camera: true, micro: true };
  state.synced = { camera: true, micro: true };
}

export function apiDevicesUpdate(update: { camera?: boolean; micro?: boolean }) {
  if (update.camera !== undefined) state.desired.camera = update.camera;
  if (update.micro !== undefined) state.desired.micro = update.micro;
  void syncDevices();
}

let running = false;

async function syncDevices() {
  if (running || isRecorder.value || !needsSync()) return;
  running = true;
  const gen = generation;
  try {
    let attempts = 0;
    while (needsSync() && gen === generation) {
      const changes = pendingChanges();
      attempts++;
      try {
        await apiRequest(changes);
        if (gen !== generation) return;
        applySynced(changes);
      } catch (err) {
        console.log("syncDevices error", err);
        if (gen !== generation) return;
        await delay(Math.min(100 * attempts, 3000));
      }
    }
  } finally {
    running = false;
    // Обновление, прилетевшее в момент выхода из цикла (в т.ч. после смены
    // поколения cleanup/reconnect), не должно теряться: needsSync() уже требует
    // state.connected, повтор захватит актуальное поколение и сойдётся.
    if (needsSync()) void syncDevices();
  }
}

function needsSync() {
  return (
    state.connected &&
    (state.desired.camera !== state.synced.camera ||
      state.desired.micro !== state.synced.micro)
  );
}

function pendingChanges() {
  const changes: { camera?: boolean; micro?: boolean } = {};
  if (state.desired.camera !== state.synced.camera) {
    changes.camera = state.desired.camera;
  }
  if (state.desired.micro !== state.synced.micro) {
    changes.micro = state.desired.micro;
  }
  return changes;
}

function applySynced(changes: { camera?: boolean; micro?: boolean }) {
  if (changes.camera !== undefined) state.synced.camera = changes.camera;
  if (changes.micro !== undefined) state.synced.micro = changes.micro;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function apiRequest(device: { camera?: boolean; micro?: boolean }) {
  await fetcher.post({
    path: "/active-room/update-device-state",
    payload: {
      user_id: authState.id,
      room_id: roomState.id,
      micro: device.micro,
      camera: device.camera,
    },
  });
}
